import React, { useState, useMemo, useEffect } from "react";
import {
  Alert,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { sumBy } from "lodash";
import GeneralLoading from "../../../shared-components/Loading";
import { BarChart, LineChart } from "@mui/x-charts";

dayjs.extend(isoWeek);

const rollingAverage = (data, windowSize = 7) => {
  if (!data?.length) return [];

  return data.map((point, i) => {
    const start = Math.max(0, i - windowSize + 1);
    const slice = data.slice(start, i + 1);

    const avg = slice.reduce((sum, p) => sum + p.y, 0) / slice.length;

    return {
      x: point.x,
      y: avg,
    };
  });
};

const formatDatesForAxis = ({ value, groupByPeriod }) => {
  const date = new Date(value);

  if (groupByPeriod === "day" || groupByPeriod === "week") {
    return dayjs(date).format("DD MMM YY");
  }

  if (groupByPeriod === "month") {
    return dayjs(date).format("MMM YYYY"); // e.g., Jan 2026
  }

  return dayjs(date).format("DD MMM YYYY");
};

const FruitifyLineChart = ({ aggregatedData, trendData, groupByPeriod }) => {
  const theme = useTheme();
  const dataset = aggregatedData.map((d, i) => ({
    x: d.x,
    y: d.y,
    trend: trendData[i]?.y,
  }));

  return (
    <LineChart
      dataset={dataset}
      xAxis={[
        {
          dataKey: "x",
          valueFormatter: (value) =>
            formatDatesForAxis({ value, groupByPeriod }),
        },
      ]}
      yAxis={[{ width: 50, min: 0 }]}
      series={[
        {
          dataKey: "y",
          label: "Metric",
          color: theme.palette.primary.main,
          showMark: false,
        },
        {
          type: "line",
          dataKey: "trend",
          label: "Trend",
          curve: "monotoneX",
          color: theme.palette.secondary.main,
          showMark: false,
        },
      ]}
      height={350}
      hideLegend
    />
  );
};

const FruitifyBarChart = ({
  aggregatedData,
  trendData,
  groupByPeriod = "day",
  color,
}) => {
  const theme = useTheme();
  const dataset = aggregatedData.map((d, i) => ({
    x: d.x,
    y: d.y,
    trend: trendData[i]?.y,
  }));
  return (
    <BarChart
      dataset={dataset}
      xAxis={[
        {
          dataKey: "x",
          valueFormatter: (value) =>
            formatDatesForAxis({ value, groupByPeriod }),
        },
      ]}
      yAxis={[{ width: 50, min: 0 }]}
      series={[
        {
          dataKey: "y",
          label: "Metric",
          color: color || theme.palette.primary.main,
        },
        {
          type: "line",
          dataKey: "trend",
          label: "Trend",
          curve: "monotoneX",
          color: theme.palette.secondary.main,
        },
      ]}
      height={350}
      barSize={24} // control width of bars
      spacing={0.2} // space between bars
      showTooltip
      hideLegend
    />
  );
};

export const MetricCharts = ({
  metrics,
  error,
  loading,
  weeklyOnly = false,
}) => {
  const [chartType, setChartType] = useState("line"); // line or bar
  const [groupByPeriod, setGroupByPeriod] = useState("day"); // day, week, month

  useEffect(() => {
    if (weeklyOnly) {
      setGroupByPeriod("week");
    }
  }, [weeklyOnly]);

  // Aggregate data
  const aggregatedData = useMemo(() => {
    if (groupByPeriod === "day") {
      return metrics
        ?.map((d) => ({ x: new Date(d.date), y: d.value }))
        .sort((a, b) => a.x - b.x);
    }

    const grouped = {};
    metrics?.forEach((d) => {
      const date = dayjs(d.date);
      let keyDate;

      if (groupByPeriod === "week") {
        // Use start of ISO week as the Date
        keyDate = date.startOf("isoWeek").toDate();
      } else if (groupByPeriod === "month") {
        keyDate = date.startOf("month").toDate();
      } else {
        keyDate = date.toDate();
      }

      const key = keyDate.getTime(); // timestamp for grouping
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(d);
    });

    return Object.entries(grouped)
      .map(([k, points]) => ({
        x: new Date(Number(k)), // x-axis must be Date
        y: sumBy(points, "value"),
      }))
      .sort((a, b) => a.x - b.x);
  }, [metrics, groupByPeriod]);

  const trendData = useMemo(() => {
    const windowSize =
      groupByPeriod === "day" ? 7 : groupByPeriod === "week" ? 4 : 3;

    return rollingAverage(aggregatedData, windowSize);
  }, [aggregatedData, groupByPeriod]);

  if (loading) return <GeneralLoading />;
  if (error)
    return (
      <Alert severity="error">
        There has been a problem: {error.message || String(error)}
      </Alert>
    );
  if (!metrics || metrics.length === 0) return <Alert>No data available</Alert>;

  return (
    <Box sx={{ mt: 2 }}>
      {/* Controls */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={(_, val) => val && setChartType(val)}
        >
          <ToggleButton value="line">Line</ToggleButton>
          <ToggleButton value="bar">Bar</ToggleButton>
        </ToggleButtonGroup>

        <Select
          value={groupByPeriod}
          onChange={(e) => setGroupByPeriod(e.target.value)}
          disabled={weeklyOnly}
        >
          <MenuItem value="day">Day</MenuItem>
          <MenuItem value="week">Week</MenuItem>
          <MenuItem value="month">Month</MenuItem>
        </Select>
      </Box>
      {chartType === "line" && (
        <FruitifyLineChart
          aggregatedData={aggregatedData}
          groupByPeriod={groupByPeriod}
          trendData={trendData}
        />
      )}
      {chartType === "bar" && (
        <FruitifyBarChart
          aggregatedData={aggregatedData}
          groupByPeriod={groupByPeriod}
          trendData={trendData}
        />
      )}
    </Box>
  );
};
